import { useQuery } from '@tanstack/react-query';
import { repoMarginCallService } from '@/services/trades/repo-margin-call-service';
import { tradesKeys } from '@/services/query-keys/trades-keys';
import type { RepoMarginCallPreview, RepoMarginCallPriceOverride } from '@/types';

interface RepoMarginCallSubsetParams {
  counterpartyName: string | undefined;
  currency: string;
  /** Omit/empty → whole book (no position_ids sent → backend uses the full-book, partial-skip path). */
  positionIds?: number[];
  priceOverrides?: RepoMarginCallPriceOverride[];
}

/**
 * POST /repo-margin-calls/preview/ — authoritative net-of-pool preview for a SELECTED subset of the
 * counterparty's repo positions (+ New Price overrides), LR-548. Drives the Post dialog's prefill
 * amount/direction; the whole-book GET preview (useRepoMarginCallPreview) stays the always-on
 * aggregate-bar source. Prices for the payment leg are still resolved client-side in the dialog, so
 * this body carries only the selection — not payment_currency/payment_fx_rate.
 *
 * positionIds/overrides are sorted so the query key is stable across selection order; `enabled`
 * should be gated on the dialog being open so this POST fires only when a post is imminent.
 */
export const useRepoMarginCallPreviewSubset = (
  { counterpartyName, currency, positionIds, priceOverrides }: RepoMarginCallSubsetParams,
  enabled = true,
) => {
  const sortedIds =
    positionIds && positionIds.length ? [...positionIds].sort((a, b) => a - b) : null;
  const sortedOverrides =
    priceOverrides && priceOverrides.length
      ? [...priceOverrides].sort((a, b) => a.position_id - b.position_id)
      : null;
  return useQuery<RepoMarginCallPreview>({
    queryKey: tradesKeys.repoMarginCallPreviewSubset(
      counterpartyName ?? '',
      currency,
      sortedIds,
      sortedOverrides,
    ),
    queryFn: () =>
      repoMarginCallService.previewSubset({
        counterparty_name: counterpartyName as string,
        currency,
        ...(sortedIds ? { position_ids: sortedIds } : {}),
        ...(sortedOverrides ? { price_overrides: sortedOverrides } : {}),
      }),
    enabled: enabled && !!counterpartyName && !!currency,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
};
